import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { trocarSenha } from '../../services/senhaService';
import './TrocaSenhaObrigatoria.css';

interface SenhaCriterios {
  minLength: boolean;
  maxLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
}

interface FormErrors {
  novaSenha?: string;
  confirmacao?: string;
  general?: string;
}

function getDashboardPath(tipo: string): string {
  switch (tipo) {
    case 'Cliente':
      return '/empreendimentos';
    case 'Arquiteto':
      return '/painel';
    case 'Assistente':
      return '/admin';
    case 'Master':
      return '/admin/conteudo';
    default:
      return '/';
  }
}

function validarCriterios(senha: string): SenhaCriterios {
  return {
    minLength: senha.length >= 8,
    maxLength: senha.length <= 64,
    hasUppercase: /[A-Z]/.test(senha),
    hasLowercase: /[a-z]/.test(senha),
    hasNumber: /[0-9]/.test(senha),
  };
}

function todosAtendidos(criterios: SenhaCriterios): boolean {
  return (
    criterios.minLength &&
    criterios.maxLength &&
    criterios.hasUppercase &&
    criterios.hasLowercase &&
    criterios.hasNumber
  );
}

function TrocaSenhaObrigatoria() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [criterios, setCriterios] = useState<SenhaCriterios>({
    minLength: false,
    maxLength: true,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
  });

  function handleSenhaChange(valor: string) {
    setNovaSenha(valor);
    setCriterios(validarCriterios(valor));
    if (errors.novaSenha) setErrors((prev) => ({ ...prev, novaSenha: undefined }));
  }

  function handleConfirmacaoChange(valor: string) {
    setConfirmacao(valor);
    if (errors.confirmacao) setErrors((prev) => ({ ...prev, confirmacao: undefined }));
  }

  function validateForm(): FormErrors {
    const newErrors: FormErrors = {};
    const crit = validarCriterios(novaSenha);

    if (!novaSenha) {
      newErrors.novaSenha = 'Nova senha é obrigatória';
    } else if (!todosAtendidos(crit)) {
      newErrors.novaSenha = 'A senha não atende todos os critérios';
    }

    if (!confirmacao) {
      newErrors.confirmacao = 'Confirmação de senha é obrigatória';
    } else if (novaSenha !== confirmacao) {
      newErrors.confirmacao = 'As senhas não conferem';
    }

    return newErrors;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      await trocarSenha(novaSenha);
      // Limpar flag de troca obrigatória no contexto
      updateUser({ requerTrocaSenha: false });
      const destino = user ? getDashboardPath(user.tipo) : '/';
      navigate(destino, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao trocar a senha.';
      setErrors({ general: message });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleGoogleLogin() {
    // Redirect to Google OAuth flow for account association
    window.location.href = '/api/auth/google?associar=true';
  }

  function handleFacebookLogin() {
    // Redirect to Facebook OAuth flow for account association
    window.location.href = '/api/auth/facebook?associar=true';
  }

  return (
    <div className="troca-senha-page">
      <div className="troca-senha-card">
        <header className="troca-senha-card__header">
          <h1 className="troca-senha-card__title">Troca de Senha Obrigatória</h1>
          <p className="troca-senha-card__subtitle">
            Defina uma nova senha para continuar acessando o sistema
          </p>
        </header>

        <form className="troca-senha-form" onSubmit={handleSubmit} noValidate>
          {errors.general && (
            <div className="troca-senha-form__error--general" role="alert">
              {errors.general}
            </div>
          )}

          <div className="troca-senha-form__field">
            <label className="troca-senha-form__label" htmlFor="nova-senha">
              Nova Senha
            </label>
            <input
              id="nova-senha"
              className="troca-senha-form__input"
              type="password"
              placeholder="Mínimo 8 caracteres"
              maxLength={64}
              value={novaSenha}
              onChange={(e) => handleSenhaChange(e.target.value)}
              aria-invalid={!!errors.novaSenha}
              aria-describedby="nova-senha-criterios"
              autoComplete="new-password"
            />
            {errors.novaSenha && (
              <span className="troca-senha-form__error" role="alert">
                {errors.novaSenha}
              </span>
            )}
          </div>

          <div className="troca-senha-criterios" id="nova-senha-criterios" aria-live="polite">
            <span className={`troca-senha-criterios__item ${criterios.minLength ? 'troca-senha-criterios__item--ok' : ''}`}>
              {criterios.minLength ? '✓' : '✗'} Mínimo 8 caracteres
            </span>
            <span className={`troca-senha-criterios__item ${criterios.maxLength ? 'troca-senha-criterios__item--ok' : ''}`}>
              {criterios.maxLength ? '✓' : '✗'} Máximo 64 caracteres
            </span>
            <span className={`troca-senha-criterios__item ${criterios.hasUppercase ? 'troca-senha-criterios__item--ok' : ''}`}>
              {criterios.hasUppercase ? '✓' : '✗'} Pelo menos 1 letra maiúscula
            </span>
            <span className={`troca-senha-criterios__item ${criterios.hasLowercase ? 'troca-senha-criterios__item--ok' : ''}`}>
              {criterios.hasLowercase ? '✓' : '✗'} Pelo menos 1 letra minúscula
            </span>
            <span className={`troca-senha-criterios__item ${criterios.hasNumber ? 'troca-senha-criterios__item--ok' : ''}`}>
              {criterios.hasNumber ? '✓' : '✗'} Pelo menos 1 número
            </span>
          </div>

          <div className="troca-senha-form__field">
            <label className="troca-senha-form__label" htmlFor="confirmacao-senha">
              Confirmar Nova Senha
            </label>
            <input
              id="confirmacao-senha"
              className="troca-senha-form__input"
              type="password"
              placeholder="Repita a nova senha"
              maxLength={64}
              value={confirmacao}
              onChange={(e) => handleConfirmacaoChange(e.target.value)}
              aria-invalid={!!errors.confirmacao}
              aria-describedby={errors.confirmacao ? 'confirmacao-senha-error' : undefined}
              autoComplete="new-password"
            />
            {errors.confirmacao && (
              <span id="confirmacao-senha-error" className="troca-senha-form__error" role="alert">
                {errors.confirmacao}
              </span>
            )}
          </div>

          <button
            type="submit"
            className="troca-senha-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Definir Nova Senha'}
          </button>

          <div className="troca-senha-divider">
            <span className="troca-senha-divider__line" />
            <span className="troca-senha-divider__text">ou associe uma conta</span>
            <span className="troca-senha-divider__line" />
          </div>

          <div className="troca-senha-social">
            <button
              type="button"
              className="troca-senha-social__button troca-senha-social__button--google"
              onClick={handleGoogleLogin}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.581-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                />
              </svg>
              Associar conta Google
            </button>

            <button
              type="button"
              className="troca-senha-social__button troca-senha-social__button--facebook"
              onClick={handleFacebookLogin}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path
                  fill="#ffffff"
                  d="M18 9a9 9 0 1 0-10.406 8.89v-6.288H5.309V9h2.285V7.017c0-2.255 1.343-3.501 3.4-3.501.984 0 2.014.176 2.014.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.399 2.602h-2.097v6.289A9.002 9.002 0 0 0 18 9z"
                />
              </svg>
              Associar conta Facebook
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrocaSenhaObrigatoria;
